// vim: set ts=2 sts=2 sw=2 et:

import React from "react";

import { JUDGE_POSITIONS } from "../../judging/resolveJudgeVotes";
import type { LiftProgress, LiftProgressRow } from "../../live/liftProgress";
import type { JudgeLights } from "../../live/platformLiveProtocol";
import { formatKg, getRecordView } from "./overlayView";

type RecordStyle = React.CSSProperties & { "--record-color"?: string; "--record-text"?: string };

export const recordStyle = (color: string | undefined, text?: string): RecordStyle | undefined =>
  color === undefined ? undefined : { "--record-color": color, "--record-text": text };

export const JudgeLightsBox = ({ lights, className }: { lights: JudgeLights; className: string }) => (
  <section className={className} aria-label="Luces de los jueces">
    {JUDGE_POSITIONS.map((position) => (
      <span key={position} data-light={lights[position]} aria-label={`${position}: ${lights[position]}`} />
    ))}
  </section>
);

export const ClockBox = ({ seconds, running, className }: { seconds: number; running: boolean; className: string }) => {
  const state = !running ? "idle" : seconds <= 10 ? "danger" : "running";
  return (
    <section className={className} data-state={state} aria-label="Tiempo del intento">
      {seconds}
    </section>
  );
};

// Celdas del movimiento en curso. Muestra el 4º intento solo si fue pedido.
// Tres casillas, o cuatro si hay cuarto intento (de récord) cargado o juzgado.
export const attemptCellCount = (row: LiftProgressRow): 3 | 4 => {
  const fourth = row.attempts[3];
  return fourth !== undefined && (fourth.weightKg > 0 || fourth.status !== "pending") ? 4 : 3;
};

export const AttemptCells = ({
  row,
  attemptNumber,
  className,
}: {
  row: LiftProgressRow;
  attemptNumber: number;
  className: string;
}) => {
  const count = attemptCellCount(row);
  return (
    <ol className={className} aria-label="Intentos del movimiento en curso">
      {Array.from({ length: count }, (_, index) => {
        const item = row.attempts[index] ?? { weightKg: 0, status: "pending" as const };
        const empty = item.status === "pending" && item.weightKg <= 0;
        const record = item.recordAttempt === true ? getRecordView(item.recordCategory) : null;
        return (
          <li
            key={index}
            data-status={empty ? "empty" : item.status}
            data-current={row.current && index === attemptNumber - 1 ? "true" : undefined}
            data-record={record === null ? undefined : "true"}
            style={recordStyle(record?.color, record?.textColor)}
          >
            {empty ? "—" : formatKg(item.weightKg)}
          </li>
        );
      })}
    </ol>
  );
};

export const bestLabel = (row: LiftProgressRow): string => {
  if (row.state === "bombed") return "Nulo";
  return row.bestKg === null ? "—" : formatKg(row.bestKg);
};

export const totalLabel = (progress: LiftProgress): string =>
  progress.totalKg === null ? "—" : formatKg(progress.totalKg);

export const RecordTag = ({ row, className }: { row: LiftProgressRow; className: string }) => {
  const record = getRecordView(row.recordCategory);
  if (record === null) return null;
  return (
    <span className={className} style={recordStyle(record.color, record.textColor)}>
      {record.label}
    </span>
  );
};
